import type { Config, Effect } from '@ml/config';
import { coordOf } from '../board/grid.ts';
import { createInitialBoard, ensureMoves } from '../board/moves.ts';
import { applyGravity, refill } from '../board/spawn.ts';
import type { Board, Cell } from '../board/types.ts';
import { Mulberry32, weightedPick, type Rng } from '../rng.ts';
import { linkContextOf, type Hero } from '../resolve/classify.ts';
import { applyDefense } from '../resolve/damage.ts';
import { applyBoardDebuff } from '../resolve/fx.ts';
import {
  defaultTarget,
  resolveRelease,
  type ResolveResult,
  type Target,
} from '../resolve/resolve.ts';
import type {
  BattleAction,
  BattleEvent,
  BattleState,
  HeroBattleState,
  HeroDamage,
  Intent,
  MonsterDef,
  MonsterState,
  StepResult,
} from './types.ts';

type BoardDebuff = Extract<
  Effect,
  { type: 'lockOrbs' | 'freezeOrbs' | 'deadMark' | 'destroyOrbs' }
>;
type Mutable<T> = { -readonly [K in keyof T]: T[K] };

/** Weighted: `weightedPick` trên chỉ số intent, trọng số thiếu = 1 (combat.md 5.1). */
function drawIntent(rng: Rng, intents: readonly Intent[]): number {
  return weightedPick(
    rng,
    intents.map((_, i) => i),
    (i) => intents[i]!.weight ?? 1,
  );
}

/** Monster vào slot: intent đầu (cycle) hoặc rút (weighted), đếm ngược = countdown của nó. */
export function spawnMonster(def: MonsterDef, rng: Rng): MonsterState {
  const intentIndex = def.pattern === 'weighted' ? drawIntent(rng, def.intents) : 0;
  return {
    id: def.id,
    hp: def.hp,
    maxHp: def.hp,
    atk: def.atk,
    def: def.def,
    res: def.res,
    element: def.element,
    pattern: def.pattern,
    intents: def.intents,
    intentIndex,
    countdown: def.intents[intentIndex]!.countdown,
    stun: 0,
  };
}

export function nextIntentIndex(m: MonsterState, rng: Rng): number {
  return m.pattern === 'weighted'
    ? drawIntent(rng, m.intents)
    : (m.intentIndex + 1) % m.intents.length;
}

export function toTargets(monsters: readonly MonsterState[]): Target[] {
  return monsters.map((m) => ({ hp: m.hp, def: m.def, res: m.res, element: m.element }));
}

export type CreateBattleInput = {
  readonly hero: Hero;
  readonly waves: readonly (readonly MonsterDef[])[];
  readonly seed: number;
  readonly config: Config;
  /** HP đầu trận (mang từ tầng trước); thiếu = HP tối đa. */
  readonly heroHp?: number;
  readonly ultGainPct?: number;
  /** Bảng dựng sẵn (test/golden); thiếu thì sinh bảng bằng seed (core-loop 3.5). */
  readonly board?: Board;
};

/** Bắt đầu trận (combat.md 6): sinh bảng → wave 1 vào slot → gán intent đầu, `round = 1`. */
export function createBattle(input: CreateBattleInput): BattleState {
  const { hero, waves, config } = input;
  if (waves.length === 0 || waves.some((w) => w.length === 0)) {
    throw new RangeError('createBattle: cần ít nhất 1 wave, mỗi wave ít nhất 1 monster');
  }
  const rng = new Mulberry32(input.seed);
  const heroState: HeroBattleState = {
    hp: Math.min(input.heroHp ?? hero.stats.hp, hero.stats.hp),
    maxHp: hero.stats.hp,
    shield: 0,
    shieldTurns: 0,
    gauge: 0,
    cooldowns: hero.skills.map(() => 0),
  };
  const board =
    input.board ?? createInitialBoard(rng, config, linkContextOf(hero, heroState, config));
  const monsters = waves[0]!.map((d) => spawnMonster(d, rng));
  return {
    hero,
    heroState,
    board,
    waves,
    wave: 0,
    monsters,
    round: 1,
    status: 'ongoing',
    rng: rng.state,
    ultGainPct: input.ultGainPct ?? 0,
  };
}

/** Một vòng đang chạy: bản đổi được của state + danh sách event. */
class RoundWork {
  board: Board;
  hero: Mutable<HeroBattleState>;
  monsters: MonsterState[];
  wave: number;
  status: BattleState['status'] = 'ongoing';
  lostReason: BattleState['lostReason'];
  readonly events: BattleEvent[] = [];
  /** Ô có lớp phủ áp trong vòng này (không tick ở P4 cùng vòng). Chỉ số phẳng. */
  fresh = new Set<number>();
  /** Slot skill vừa cast trong vòng này (hồi chiêu không giảm ở P4 cùng vòng). */
  readonly freshCooldown = new Set<number>();

  constructor(
    readonly state: BattleState,
    readonly rng: Mulberry32,
    readonly config: Config,
  ) {
    this.board = state.board;
    this.hero = { ...state.heroState, cooldowns: state.heroState.cooldowns.slice() };
    this.monsters = state.monsters.slice();
    this.wave = state.wave;
  }

  setMonster(slot: number, patch: Partial<MonsterState>): void {
    this.monsters[slot] = { ...this.monsters[slot]!, ...patch };
  }

  /** Sát thương lên hero: phòng thủ (combat.md 5.2) → khiên hấp thụ → HP (chặn ở 0). */
  damageHero(raw: number): HeroDamage {
    const damage = applyDefense(raw, this.state.hero.stats.defense, this.config);
    const absorbed = Math.min(this.hero.shield, damage);
    this.hero.shield -= absorbed;
    this.hero.hp = Math.max(0, this.hero.hp - (damage - absorbed));
    return { damage, absorbed, heroHp: this.hero.hp };
  }

  lose(reason: 'hp' | 'maxRounds'): void {
    this.status = 'lost';
    this.lostReason = reason;
    this.events.push({ type: 'battleLost', round: this.state.round, reason });
  }

  /** Gravity + refill nếu bảng có ô rỗng; lớp phủ "mới" đi theo viên khi rơi. */
  settle(): void {
    if (!this.board.cells.some((c) => c === null)) return;
    const g = applyGravity(this.board);
    if (g.moves.length) {
      this.events.push({ type: 'fall', moves: g.moves });
      const w = this.board.width;
      const map = new Map(g.moves.map((m) => [m.from.y * w + m.from.x, m.to.y * w + m.to.x]));
      this.fresh = new Set([...this.fresh].map((i) => map.get(i) ?? i));
    }
    const r = refill(g.board, this.rng, this.config);
    this.events.push({ type: 'spawn', cells: r.spawned });
    this.board = r.board;
  }

  // ---------------- P1 ----------------

  applyPlayer(r: ResolveResult, action: BattleAction): void {
    const prevTargets = toTargets(this.monsters);
    this.board = r.board;
    r.targets.forEach((t, i) => this.setMonster(i, { hp: t.hp }));
    const kind = r.action.kind;
    let gauge = r.gauge;
    if (kind !== 'ULTIMATE' && this.state.ultGainPct > 0) {
      const gain = r.gauge - this.hero.gauge;
      gauge = Math.min(
        this.config.combat.ult.gaugeMax,
        this.hero.gauge + Math.floor((gain * (100 + this.state.ultGainPct)) / 100),
      );
    }
    this.hero.gauge = gauge;
    this.hero.cooldowns = r.cooldowns.slice();
    if (kind === 'ABILITY') this.freshCooldown.add(r.action.slot!);
    this.events.push({
      type: 'player',
      action: kind,
      slot: r.action.slot,
      crit: r.crit,
      total: r.total,
      gauge,
      events: r.events,
    });
    const slot = r.action.slot;
    if (slot === undefined) return;
    const skill = slot === 3 ? this.state.hero.ultimate : this.state.hero.skills[slot]!;
    for (const e of r.pendingEffects) {
      const maxHp = this.hero.maxHp;
      if (e.type === 'heal') {
        const amount = Math.floor((maxHp * e.pctMaxHp) / 100);
        this.hero.hp = Math.min(maxHp, this.hero.hp + amount);
        this.events.push({ type: 'heroHeal', amount, heroHp: this.hero.hp });
      } else if (e.type === 'shield') {
        const amount = Math.floor((maxHp * e.pctMaxHp) / 100);
        this.hero.shield = amount;
        this.hero.shieldTurns = e.turns;
        this.events.push({ type: 'heroShield', amount, turns: e.turns });
      } else if (e.type === 'stun') {
        for (const s of this.skillTargets(skill.target, r, prevTargets, action.target)) {
          const turns = Math.max(this.monsters[s]!.stun, e.turns);
          this.setMonster(s, { stun: turns });
          this.events.push({ type: 'stun', slot: s, turns });
        }
      } else {
        this.events.push({ type: 'effectSkipped', source: 'hero', effect: e.type });
      }
    }
  }

  /** Mục tiêu của skill (combat.md 3.5, 4.1) còn sống sau đòn. */
  skillTargets(
    target: 'single' | 'all' | 'random',
    r: ResolveResult,
    prev: readonly Target[],
    preferred: number | undefined,
  ): number[] {
    let list: number[];
    if (target === 'all') list = prev.flatMap((t, i) => (t.hp > 0 ? [i] : []));
    else if (target === 'random') {
      const ev = r.events.find((e) => e.type === 'randomTarget');
      list = ev ? [ev.target] : [];
    } else list = [defaultTarget(prev, this.config, preferred)];
    return list.filter((i) => i >= 0 && this.monsters[i]!.hp > 0);
  }

  // ---------------- P2 ----------------

  /** true: wave vừa dọn xong (đã đưa wave kế vào hoặc thắng). */
  checkWave(): boolean {
    if (this.monsters.some((m) => m.hp > 0)) return false;
    this.events.push({ type: 'waveCleared', wave: this.wave });
    const next = this.state.waves[this.wave + 1];
    if (!next) {
      this.status = 'won';
      this.events.push({ type: 'battleWon', round: this.state.round });
      return true;
    }
    this.wave++;
    this.monsters = next.map((d) => spawnMonster(d, this.rng));
    this.events.push({ type: 'waveStart', wave: this.wave, monsters: next.map((d) => d.id) });
    return true;
  }

  // ---------------- P3 ----------------

  monsterPhase(): void {
    for (let slot = 0; slot < this.monsters.length; slot++) {
      const m = this.monsters[slot]!;
      if (m.hp <= 0) continue;
      if (m.stun > 0) {
        this.setMonster(slot, { stun: m.stun - 1 });
        this.events.push({ type: 'stunned', slot, stunLeft: m.stun - 1 });
        continue;
      }
      const countdown = m.countdown - 1;
      this.setMonster(slot, { countdown });
      this.events.push({ type: 'countdown', slot, countdown });
      if (countdown > 0) continue;
      const intent = m.intents[m.intentIndex]!;
      this.events.push({ type: 'intent', slot, intent: intent.id });
      for (const e of intent.effects) {
        this.monsterEffect(slot, e);
        if (this.status === 'lost') return;
      }
      const idx = nextIntentIndex(this.monsters[slot]!, this.rng);
      const next = m.intents[idx]!;
      this.setMonster(slot, { intentIndex: idx, countdown: next.countdown });
      this.events.push({ type: 'nextIntent', slot, intent: next.id, countdown: next.countdown });
    }
  }

  monsterEffect(slot: number, e: Effect): void {
    const m = this.monsters[slot]!;
    switch (e.type) {
      case 'attack':
        for (let hit = 0; hit < e.hits; hit++) {
          const d = this.damageHero(Math.floor((m.atk * e.powerPct) / 100));
          this.events.push({ type: 'monsterAttack', slot, hit, ...d });
          if (this.hero.hp <= 0) return this.lose('hp');
        }
        return;
      case 'lockOrbs':
      case 'freezeOrbs':
      case 'deadMark':
      case 'destroyOrbs':
        return this.boardDebuff(slot, e);
      case 'heal': {
        const amount = Math.floor((m.maxHp * e.pctMaxHp) / 100);
        const hp = Math.min(m.maxHp, m.hp + amount);
        this.setMonster(slot, { hp });
        this.events.push({ type: 'monsterHeal', slot, amount, hp });
        return;
      }
      default:
        this.events.push({ type: 'effectSkipped', source: 'monster', effect: e.type });
    }
  }

  boardDebuff(slot: number, e: BoardDebuff): void {
    const m = this.monsters[slot]!;
    const dmg = e.type === 'deadMark' ? Math.floor((m.atk * e.powerPct) / 100) : 0;
    const r = applyBoardDebuff(this.board, e, this.rng, this.config, dmg);
    this.board = r.board;
    const w = this.board.width;
    const idx = r.coords.map((c) => c.y * w + c.x);
    if (e.type === 'destroyOrbs') for (const i of idx) this.fresh.delete(i);
    else for (const i of idx) this.fresh.add(i);
    this.events.push({
      type: 'boardDebuff',
      slot,
      effect: e.type,
      coords: r.coords,
      events: r.events,
    });
    if (e.type === 'destroyOrbs') this.settle();
  }

  // ---------------- P4 ----------------

  endOfRound(): void {
    const skipFresh = !this.config.battle.newDebuffTicksSameRound;
    const isFresh = (i: number) => skipFresh && this.fresh.has(i);
    const cells = this.board.cells.slice() as (Cell | null)[];
    const at = (i: number) => coordOf(this.board, i);
    // 1. đánh dấu tử, thứ tự hàng
    cells.forEach((cell, i) => {
      if (cell?.t !== 'orb' || cell.overlay?.type !== 'deadMark' || isFresh(i)) return;
      const turns = cell.overlay.turns - 1;
      if (turns > 0) {
        cells[i] = { ...cell, overlay: { ...cell.overlay, turns } };
        this.events.push({ type: 'deadMarkTick', at: at(i), turns });
        return;
      }
      cells[i] = null;
      this.events.push({
        type: 'deadMarkExplode',
        at: at(i),
        ...this.damageHero(cell.overlay.damage),
      });
    });
    // 2. khoá có thời hạn (0 = vĩnh viễn)
    cells.forEach((cell, i) => {
      if (cell?.t !== 'orb' || cell.overlay?.type !== 'lock' || cell.overlay.turns <= 0) return;
      if (isFresh(i)) return;
      const turns = cell.overlay.turns - 1;
      if (turns > 0) {
        cells[i] = { ...cell, overlay: { type: 'lock', turns } };
        this.events.push({ type: 'lockTick', at: at(i), turns });
      } else {
        cells[i] = { t: 'orb', kind: cell.kind, color: cell.color };
        this.events.push({ type: 'unlock', at: at(i) });
      }
    });
    this.board = { width: this.board.width, height: this.board.height, cells };
    // 3. trạng thái có `turns` + hồi chiêu
    if (this.hero.shieldTurns > 0) {
      this.hero.shieldTurns--;
      if (this.hero.shieldTurns === 0) {
        this.hero.shield = 0;
        this.events.push({ type: 'shieldExpired' });
      }
    }
    this.hero.cooldowns = this.hero.cooldowns.map((c, s) =>
      c > 0 && !this.freshCooldown.has(s) ? c - 1 : c,
    );
    // 4. gravity + refill, rồi bảo đảm còn nước (core-loop 3.5)
    this.settle();
    const ctx = linkContextOf(this.state.hero, this.hero, this.config);
    const em = ensureMoves(this.board, this.rng, this.config, ctx);
    if (em.action !== 'none') {
      this.events.push({ type: 'shuffle', action: em.action, attempts: em.attempts });
    }
    this.board = em.board;
    // 5. thua?
    if (this.hero.hp <= 0) this.lose('hp');
  }

  build(round: number): BattleState {
    const s: Mutable<BattleState> = {
      ...this.state,
      heroState: { ...this.hero },
      board: this.board,
      wave: this.wave,
      monsters: this.monsters,
      round,
      status: this.status,
      rng: this.rng.state,
    };
    if (this.lostReason) s.lostReason = this.lostReason;
    return s;
  }
}

/**
 * Một bước trận (core-loop: `step(state, action, config) -> { state, events }`), chạy trọn một
 * vòng P1 → P4 (combat.md 6). Hàm thuần: không sửa `state`; RNG lấy từ `state.rng`. Hành động
 * không hợp lệ trả lại đúng object `state` (không tốn lượt, monster không hành động).
 */
export function stepBattle(state: BattleState, action: BattleAction, config: Config): StepResult {
  if (state.status !== 'ongoing') {
    return { state, events: [{ type: 'invalid', reason: 'battleOver' }] };
  }
  const rng = new Mulberry32(state.rng);
  const r = resolveRelease({
    board: state.board,
    path: action.path,
    hero: state.hero,
    heroState: { cooldowns: state.heroState.cooldowns, gauge: state.heroState.gauge },
    targets: toTargets(state.monsters),
    target: action.target,
    rng,
    config,
  });
  if (!r.ok && !r.consumesTurn) {
    return { state, events: [{ type: 'invalid', reason: r.reason! }] };
  }
  const w = new RoundWork(state, rng, config);
  if (r.ok) w.applyPlayer(r, action);
  else w.events.push({ type: 'invalid', reason: r.reason! });
  // MERGE không tốn lượt (essence.mergeOnlyConsumesTurn = false): không pha monster, không tick.
  if (!r.consumesTurn) return { state: w.build(state.round), events: w.events };

  const cleared = w.checkWave();
  if (w.status === 'won') return { state: w.build(state.round), events: w.events };
  if (!cleared || !config.battle.waveTransitionSkipsMonsterPhase) {
    w.monsterPhase();
    if (w.status === 'lost') return { state: w.build(state.round), events: w.events };
  }
  w.endOfRound();
  if (w.status === 'lost') return { state: w.build(state.round), events: w.events };
  w.events.push({ type: 'roundEnd', round: state.round });
  const round = state.round + 1;
  if (round > config.battle.maxRoundsPerBattle) w.lose('maxRounds');
  return { state: w.build(round), events: w.events };
}
